import { useState, useRef, useCallback, useEffect } from 'react';
import { MONACO_LAYOUT_FLUSH_EVENT } from '../components/ui/ResizableSplitter';

export const SIDEBAR_WIDTH_KEY = 'sidebarWidth';
export const SIDEBAR_MIN_WIDTH = 220;
export const SIDEBAR_MAX_WIDTH = 480;
export const SIDEBAR_DEFAULT_WIDTH = 288;

function loadSidebarWidth() {
  try {
    const saved = parseInt(localStorage.getItem(SIDEBAR_WIDTH_KEY), 10);
    if (saved >= SIDEBAR_MIN_WIDTH && saved <= SIDEBAR_MAX_WIDTH) return saved;
  } catch {
    /* ignore */
  }
  return SIDEBAR_DEFAULT_WIDTH;
}

export function clampSidebarWidth(px) {
  if (typeof window === 'undefined') return px;
  const max = Math.min(SIDEBAR_MAX_WIDTH, Math.floor(window.innerWidth * 0.45));
  return Math.max(SIDEBAR_MIN_WIDTH, Math.min(max, px));
}

/**
 * Width for a drag from `startX` to `clientX`, clamped.
 *
 * The resizer always sits on the sidebar's RIGHT edge, so dragging right always
 * widens it — there is no per-position flip. There used to be one, guarded on
 * `sidebarPosition === 'right'`, on the assumption that the sidebar moved to the
 * right-hand side in that mode. It does not: `html.sidebar-right` reverses only
 * `.app-main-layout`, whose direct children are the icon rail and the content
 * wrapper, so the RAIL moves and the sidebar column — which lives inside that
 * wrapper — stays put.
 *
 * The flip therefore inverted the drag: pulling right made the sidebar narrower
 * while the content grew leftwards. Exported so this is testable as arithmetic
 * rather than only through a mousemove.
 */
export function sidebarWidthFromDrag(startWidth, startX, clientX) {
  return clampSidebarWidth(startWidth + (clientX - startX));
}

export function useSidebarWidth() {
  const [sidebarWidth, setSidebarWidth] = useState(loadSidebarWidth);
  const [isResizing, setIsResizing] = useState(false);
  const widthRef = useRef(sidebarWidth);

  useEffect(() => {
    widthRef.current = sidebarWidth;
  }, [sidebarWidth]);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
    const startX = e.clientX;
    const startWidth = widthRef.current;

    const onMove = (ev) => {
      const next = sidebarWidthFromDrag(startWidth, startX, ev.clientX);
      widthRef.current = next;
      setSidebarWidth(next);
      window.dispatchEvent(new CustomEvent(MONACO_LAYOUT_FLUSH_EVENT));
    };

    const onUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      try {
        localStorage.setItem(SIDEBAR_WIDTH_KEY, String(widthRef.current));
      } catch {
        /* ignore */
      }
      window.dispatchEvent(new CustomEvent(MONACO_LAYOUT_FLUSH_EVENT));
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  return { sidebarWidth, isResizing, handleResizeStart };
}
